import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { buildLoginUrl, buildTrialSignupUrl } from "@/lib/pricing-config";
import { trackCTAClick } from "@/lib/gtm";

const navLinks = [
  { to: "/features", label: "Features" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/pricing", label: "Pricing" },
];

const MarketingHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  // Computed per render: both forward captured UTMs + fbclid to the app
  // hand-off — Sign In included, since /login carries the signup toggle and
  // an ad-click visitor who signs up from there must keep attribution.
  const loginUrl = buildLoginUrl();
  const trialUrl = buildTrialSignupUrl();

  const isActive = (path: string) => location.pathname === path;

  return (
    // theme-light: the header uses the app's light palette on every page,
    // ahead of the rest of the site moving off the dark theme.
    <header className="theme-light sticky top-0 z-50 w-full border-b border-border/80 bg-background">
      <div className="container mx-auto flex h-[76px] items-center justify-between px-4 sm:px-6 lg:px-8 relative">
        <Link to="/" className="flex items-center gap-2.5 rounded-md py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
          <span className="flex items-center justify-center w-8 h-8 rounded-full shadow-sm bg-inherit">
            <img src="/logo-128.webp" alt="Sidestep Ads Logo" width="24" height="24" className="rounded-sm" />
          </span>
          <span className="font-bold text-lg text-foreground tracking-tight">Sidestep Ads</span>
        </Link>
        
        <nav className="hidden md:flex items-center gap-1.5 absolute left-1/2 -translate-x-1/2" aria-label="Main navigation">
          {navLinks.map((link) => (
            <Link 
              key={link.to}
              to={link.to} 
              className={cn(
                "text-sm font-medium transition-colors rounded-lg px-4 py-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isActive(link.to)
                  ? "text-foreground bg-surface-1"
                  : "text-muted-foreground hover:text-foreground hover:bg-surface-1/50"
              )}
              aria-current={isActive(link.to) ? "page" : undefined}
            >
              {link.label}
            </Link>
          ))}
        </nav>
        
        <div className="hidden md:flex items-center gap-3">
          <a href={loginUrl}>
            <Button variant="ghost" size="sm" className="text-sm px-4">Sign In</Button>
          </a>
          <a href={trialUrl} onClick={() => trackCTAClick("start_free_trial", trialUrl, { cta_location: "header_desktop" })}>
            <Button variant="gradient" size="sm" className="text-sm px-5">
              Start Free Trial
            </Button>
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          className="md:hidden p-2.5 -mr-2.5 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border/80 bg-background">
          <nav className="container mx-auto px-4 py-5 flex flex-col gap-1" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <Link 
                key={link.to}
                to={link.to} 
                className={cn(
                  "text-sm font-medium transition-colors py-3 px-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  isActive(link.to)
                    ? "text-foreground bg-surface-1"
                    : "text-muted-foreground hover:text-foreground"
                )}
                onClick={() => setMobileMenuOpen(false)}
                aria-current={isActive(link.to) ? "page" : undefined}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-border/80">
              <a href={loginUrl}>
                <Button variant="ghost" size="sm" className="h-11 w-full justify-start">Sign In</Button>
              </a>
              <a
                href={trialUrl}
                onClick={() => {
                  trackCTAClick("start_free_trial", trialUrl, { cta_location: "header_mobile" });
                  setMobileMenuOpen(false);
                }}
                className="w-full"
              >
                <Button variant="gradient" size="sm" className="h-11 w-full">
                  Start Free Trial
                </Button>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default MarketingHeader;
